import alloyAnalytics from '@telus/alloy-analytics'

/**
 * Navigation Bar Analytics Helper Functions
 * Tracks navigation interactions across different tiers and devices
 */

export type NavigationAnalyticsParams = {
  label: string
  isMobile: boolean
  parentLabel?: string
  url?: string
}

/**
 * Converts label to lowercase with underscores
 * Replaces spaces and hyphens with underscores
 */
const formatLinkName = (label: string): string => {
  return label.toLowerCase().replace(/[\s-]+/g, '_')
}

/**
 * Strips query parameters from URL
 * Returns the base URL without query string or hash
 */
const stripQueryParams = (url: string): string => {
  // Remove everything after ? or #
  return url.split('?')[0].split('#')[0]
}

/**
 * Fires navigation analytics event using alloy-analytics package
 * Automatically determines tier based on provided parameters
 * Sends the analytics event immediately based on the current configuration
 */
export const fireNavigationAnalytics = ({ label, isMobile, parentLabel, url }: NavigationAnalyticsParams) => {
  // Validate required parameters
  if (!label || typeof label !== 'string') {
    console.error('[Navigation Analytics] Invalid label provided:', label)
    return
  }

  const linkName = formatLinkName(label)
  const toolType = isMobile ? 'mobile_nav' : 'desktop_nav'

  // Determine tier and build values based on whether there's a parent
  const tier = parentLabel ? 1 : 0
  const linkPosition = parentLabel ? formatLinkName(parentLabel) : 'sub_nav_header'
  const toolSelections = parentLabel ? `${formatLinkName(parentLabel)}>${linkName}` : linkName

  // Build the linkClicks data
  const linkClicksData = {
    webInteraction1: {
      name: `ge_sub-nav:${linkName}`,
      linkName,
      linkPosition,
      ...(url && { URL: stripQueryParams(url) }),
    },
    webInteraction2: {
      name: `ge_sub-nav:${linkName}`,
      toolName: 'telus_subnav',
      toolType,
      toolSelections,
      toolUsageStepName: `nav_tier:${tier}`,
    },
  }

  // Fire analytics using alloy-analytics package
  // Wrapped in try-catch to prevent navigation blocking if analytics fails
  try {
    alloyAnalytics
      .event('linkClicks')
      .webInteraction(linkClicksData.webInteraction1)
      .webInteraction(linkClicksData.webInteraction2)
      .fire()
  } catch (error) {
    // Log error but don't block user navigation
    console.error('[NavigationBar Analytics] Failed to fire analytics:', error)
  }
}
